import React, { useState, useRef, useEffect } from "react";
import { AIStatus } from "./types";

interface MedicationInputProps {
  onSubmit: (text: string) => void;
  onNoMedications: () => void;
  onCancel?: () => void;
  status: AIStatus;
  placeholder?: string;
  initialText?: string;
  isInline?: boolean;
}

export const MedicationInput: React.FC<MedicationInputProps> = ({
  onSubmit,
  onNoMedications,
  onCancel,
  status,
  placeholder = "Type your medications here...",
  initialText = "",
  isInline = false,
}) => {
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
    }
  };

  const isDisabled = status === AIStatus.PROCESSING || status === AIStatus.VALIDATING;

  return (
    <div className="space-y-4">
      {isInline && (
        <h4 className="text-sm font-medium text-gray-700 mb-2">Edit your medications:</h4>
      )}
      
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          aria-label="Enter your medications"
          className={`
            w-full px-4 py-3 border rounded-lg resize-none
            transition-all duration-200
            ${
              isDisabled
                ? "bg-gray-50 border-gray-200 cursor-not-allowed"
                : "bg-white border-gray-200 hover:border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            }
            placeholder-gray-400
            min-h-[120px] max-h-[300px]
          `}
          rows={4}
        />
        {(status === AIStatus.PROCESSING || status === AIStatus.VALIDATING) && (
          <div className="absolute top-3 right-3" aria-hidden="true">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || isDisabled}
          aria-busy={isDisabled}
          className="flex-1 px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {status === AIStatus.PROCESSING || status === AIStatus.VALIDATING ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Evaluating your medications...
            </div>
          ) : (
            "Check medication eligibility"
          )}
        </button>
        
        {isInline && onCancel ? (
          <button
            onClick={onCancel}
            disabled={isDisabled}
            className="flex-1 sm:flex-initial px-4 py-3 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Cancel
          </button>
        ) : (
          <button
            onClick={onNoMedications}
            disabled={isDisabled}
            className="flex-1 sm:flex-initial px-4 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            I don't take any medications
          </button>
        )}
      </div>
    </div>
  );
};